import Main from "niteroi/packages/air-passenger-rights/controllers/Main.controller";
import NumberFormat from "sap/ui/core/format/NumberFormat";
import {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import {
    ClaimStatuses,
    IClaim,
    IClaimDocument,
    IClaimFlight,
    IPassenger,
    TDocumentEncoding
} from "niteroi/packages/air-passenger-rights/models/interfaces";
import JSONModel from "sap/ui/model/json/JSONModel";
import Select, {Select$ChangeEvent} from "sap/m/Select";
import CheckBox, {CheckBox$SelectEvent} from "sap/m/CheckBox";
import Button, {Button$PressEvent} from "sap/m/Button";
import SearchField, {SearchField$ChangeEvent, SearchField$SuggestEvent} from "sap/m/SearchField";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ComboBox, {ComboBox$ChangeEvent} from "sap/m/ComboBox";
import Table from "sap/m/Table";
import Dialog from "sap/m/Dialog";
import {UploadSetwithTable$BeforeUploadStartsEvent} from "sap/m/plugins/UploadSetwithTable";
import Fragment from "sap/ui/core/Fragment";
import Input, {Input$LiveChangeEvent, Input$SuggestionItemSelectedEvent} from "sap/m/Input";
import {DateTimeInput$ChangeEvent} from "sap/m/DateTimeInput";
import DateTimePicker from "sap/m/DateTimePicker";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {ObjectIdentifier$TitlePressEvent} from "sap/m/ObjectIdentifier";
import DateFormat from "sap/ui/core/format/DateFormat";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

export default class Claims extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    private dialogDocumentViewer: Dialog | null = null;

    public onInit(): void {
        super.onInit();
        this.setModel(new JSONModel(this.getClaimStatusOptions()), "claimStatuses");
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);

            if (route.getPattern().includes("claims/add")) {
                this.setModel(this.createEmptyClaimModel(), "claim");
                this.initializeFlightMirrorState();
                this.setActiveTileByRoute();
                this.loadAirlinesForFlightCodes();
                this.loadExistingFlightsForClaim()
                this.loadAirportsForLocations();
                this.loadPassengersCatalog();
            } else {
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const pattern = event.getSource().getPattern();
        if (pattern.includes("claims/add") || pattern.includes("claims/edit")) {
            this.initializeFlightMirrorState();
            this.setActiveTileByRoute();
            this.loadAirlinesForFlightCodes();
            this.loadExistingFlightsForClaim();
            this.loadAirportsForLocations();
            this.loadPassengersCatalog();
        }
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        const url = objectId ? `/api/air-passenger-rights/claims/${objectId}` : "/api/air-passenger-rights/claims";
        const modelName = objectId ? "claim" : "claims";
        this.loadCollectionData(url, modelName);

        if (!objectId) {
            const claimsModel = this.getModel("claims");
            const claimsAnalytics = new JSONModel({});
            this.setModel(claimsAnalytics, "claimsAnalytics");
            claimsModel.attachRequestCompleted(() => {
                const data = claimsModel.getData() as Array<IClaim>;
                if (data) {
                    let potentialProfit = 0;
                    let totalCompensation = 0;
                    let totalPaidClaims = 0;
                    let totalNotEntitledClaims = 0;
                    const passengerNames: string[] = [];
                    const airlineNames: string[] = [];
                    data.forEach((claim: IClaim) => {
                        if (claim.claimStatus === ClaimStatuses.PAID) {
                            const compensation =  typeof claim.compensationAmount === "string" ? parseFloat(claim.compensationAmount) : claim.compensationAmount;
                            const fee = typeof claim.compensationFee === "string" ? parseFloat(claim.compensationFee) : claim.compensationFee;
                            potentialProfit += compensation - ((compensation * fee) / 100.00);
                            if (compensation) {
                                totalCompensation += compensation;
                            }
                            totalPaidClaims++;
                        } else if (claim.claimStatus === ClaimStatuses.NEGATIVE_RESPONSE || claim.claimStatus === ClaimStatuses.NEGATIVE_AFTER_APPEAL) {
                            totalNotEntitledClaims++;
                        }

                        claim.passengers.forEach((passenger: IPassenger) => {
                            if (passengerNames.indexOf(passenger.firstName) === -1) {
                                passengerNames.push(passenger.firstName);
                            }
                        });
                        claim.flights.forEach((flight: IClaimFlight) => {
                            if (airlineNames.indexOf(flight.originalFlightIataCode) === -1) {
                                airlineNames.push(flight.originalFlightIataCode);
                            }
                            if (airlineNames.indexOf(flight.actualFlightIataCode) === -1) {
                                airlineNames.push(flight.actualFlightIataCode);
                            }
                        });
                    });
                    const potentialProfitEuro = NumberFormat.getCurrencyInstance().format(potentialProfit, "EUR");
                    const compensationEuro = NumberFormat.getCurrencyInstance().format(totalCompensation, "EUR");
                    claimsAnalytics.setProperty("/potentialProfit", potentialProfitEuro);
                    claimsAnalytics.setProperty("/totalCompensation", compensationEuro);
                    claimsAnalytics.setProperty("/totalPaidClaims", totalPaidClaims);
                    claimsAnalytics.setProperty("/totalNotEntitled", totalNotEntitledClaims);
                    claimsAnalytics.setProperty("/passengerNames", passengerNames.length);
                    claimsAnalytics.setProperty("/airlineNames", airlineNames.length);
                }
            });
        }
    }

    public onBeforeRendering(): void {
        this.setActiveTileByRoute();
    }

    public onNavButtonPress(): void {
        this.navTo("air-passenger-rights/claims", {}, false);
    }

    public onPressButtonCreateNewClaim(): void {
        this.setModel(this.createEmptyClaimModel(), "claim");
        this.navTo("air-passenger-rights/claims/add", {}, false);
    }


    public onPressFlightIdentifier(event: ObjectIdentifier$TitlePressEvent): void {
        const source = event.getSource();
        const bindingContext = source.getBindingContext("claims");
        const flightIdentifier = bindingContext.getModel().getProperty(bindingContext.getPath()) as IClaimFlight;

        this.navTo("air-passenger-rights/flights/edit", { id: flightIdentifier.originalFlightId }, false);
    }

    public onRowSelectionChange(event: Table$RowSelectionChangeEvent): void {
        const rowContext = event.getParameter("rowContext");
        const selectedClaim = rowContext?.getObject() as IClaim | undefined;

        if (!selectedClaim?.claimId) {
            return;
        }

        this.navTo("air-passenger-rights/claims/edit", {id: selectedClaim.claimId}, false);
    }

    // ClaimsAddEdit
    public onClaimStatusChange(event: Select$ChangeEvent): void {
        this.updateNumericClaimProperty(event, "/claimStatus");
    }

    public onPressButtonSaveFormEdit(): void {
        const claimModel = this.getModel("claim");
        const claimId = claimModel.getProperty("/claimId");
        const httpMethod = claimId ? "PUT" : "POST";
        const url = claimId ? `/api/air-passenger-rights/claims/${claimId}` : "/api/air-passenger-rights/claims";
        claimModel.loadData(
            url,
            claimModel.getJSON(),
            true,
            httpMethod,
            true,
            undefined,
            this.getRequestHeaders()
        ).then(() => {
            this.showToast(this.getText("airPassengerRightsClaimsSectionClaimSaved"));
            this.navTo("air-passenger-rights/claims/edit", {id: claimModel.getProperty("/claimId")}, false);
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        });
    }

    public onOriginalFlightIncidentTypeChange(event: Select$ChangeEvent): void {
        const flightPath = this.getFlightContextPath(event);
        if (!flightPath) {
            return;
        }

        this.updateNumericClaimProperty(event, `${flightPath}/originalFlightIncidentType`, true);
        this.syncFlightActualWhenMirrored(event);
    }

    public onOriginalFlightIncidentConditionChange(event: Select$ChangeEvent): void {
        const flightPath = this.getFlightContextPath(event);
        if (!flightPath) {
            return;
        }

        this.updateNumericClaimProperty(event, `${flightPath}/originalFlightIncidentCondition`, true);
        this.syncFlightActualWhenMirrored(event);
    }

    public onActualFlightIncidentTypeChange(event: Select$ChangeEvent): void {
        const flightPath = this.getFlightContextPath(event);
        if (!flightPath) {
            return;
        }

        this.updateNumericClaimProperty(event, `${flightPath}/actualFlightIncidentType`, true);
    }

    public onActualFlightIncidentConditionChange(event: Select$ChangeEvent): void {
        const flightPath = this.getFlightContextPath(event);
        if (!flightPath) {
            return;
        }

        this.updateNumericClaimProperty(event, `${flightPath}/actualFlightIncidentCondition`, true);
    }

    public onSameAsOriginalFlightChange(event: CheckBox$SelectEvent): void {
        const flightPath = this.getFlightContextPath(event);
        if (!flightPath) {
            return;
        }

        const claimModel = this.getModel("claim");
        const isSameAsOriginalFlight = (event.getSource()).getSelected();

        claimModel.setProperty(`${flightPath}/sameAsOriginalFlight`, isSameAsOriginalFlight);
        if (isSameAsOriginalFlight) {
            this.syncFlightActualWithOriginal(claimModel, flightPath);
        }
    }

    public onAddFlightSegment(): void {
        const claimModel = this.getModel("claim");
        const flights = (claimModel.getProperty("/flights") as Array<Record<string, unknown>> | undefined) ?? [];
        claimModel.setProperty("/flights", [...flights, this.createEmptyFlightSegment()]);
    }

    public onRemoveFlightSegment(event: Button$PressEvent): void {
        const source = event.getSource();
        const flightPath = source.getBindingContext("claim")?.getPath();
        if (!flightPath) {
            return;
        }

        const segmentIndex = Number.parseInt(flightPath.split("/").pop() ?? "-1", 10);
        if (Number.isNaN(segmentIndex)) {
            return;
        }

        const claimModel = this.getModel("claim");
        const flights = (claimModel.getProperty("/flights") as Array<Record<string, unknown>> | undefined) ?? [];
        if (flights.length <= 1) {
            return;
        }

        claimModel.setProperty("/flights", flights.filter((_, index) => index !== segmentIndex));
    }

    public onSearchFieldAirportSuggest(event: SearchField$SuggestEvent): void {
        const searchQuery = event.getParameter("suggestValue").trim();
        const searchField = event.getSource();
        const binding = searchField.getBinding("suggestionItems") as ListBinding | undefined;
        this.bindFilters(binding, searchQuery);
        searchField.suggest();
    }

    public onFlightNumberSuggest(event: Input$LiveChangeEvent): void {
        const input = event.getSource() as Input;
        const value = event.getParameter("value")?.trim() || "";
        const binding = input.getBinding("suggestionItems") as ListBinding | undefined;

        if (binding && value) {
            this.bindFlightFilters(input, binding, value);
            input.fireSuggest();
        }
    }

    public onFlightNumberSuggestionSelected(event: Input$SuggestionItemSelectedEvent): void {
        const input = event.getSource() as Input;
        const selectedItem = event.getParameter("selectedItem");

        if (selectedItem) {
            const key = selectedItem.getKey();
            input.setValue(key);
            input.fireChange({ value: key });

            const flightContext = selectedItem.getBindingContext("flights");
            const flight = flightContext.getProperty(flightContext.getPath());
            const claimContext = input.getBindingContext("claim");
            const claimModel = claimContext.getModel() as JSONModel;
            const flightPrefix = input.getBindingPath("value")?.startsWith("actual") ? "actual" : "original";
            claimModel.setProperty(`${claimContext.getPath()}/${flightPrefix}FlightDepartureAt`, flight.departureAt);
            claimModel.setProperty(`${claimContext.getPath()}/${flightPrefix}FlightArrivalAt`, flight.arrivalAt)
        }
    }

    private bindFlightFilters(input: Input, binding: ListBinding, flightNumber: string): void {
        if (!binding) {
            return;
        }

        const bindingContext = input.getBindingContext("claim");
        if (!bindingContext) {
            binding.filter([]);
            return;
        }

        const flightPath = bindingContext.getPath();
        const claimModel = this.getModel("claim");
        const flightPrefix = input.getBindingPath("value")?.startsWith("actual") ? "actual" : "original";
        const selectedAirlineIataCode = claimModel.getProperty(`${flightPath}/${flightPrefix}FlightIataCode`) as string | undefined
        const filters: Filter[] = [];

        // Filter by airline IATA code if selected
        if (selectedAirlineIataCode) {
            filters.push(new Filter("iataCode", FilterOperator.EQ, selectedAirlineIataCode));
        }

        // Filter by flight number if provided
        if (flightNumber) {
            filters.push(new Filter("flightNumber", FilterOperator.Contains, flightNumber));
        }

        binding.filter(filters);
    }

    public onPassengerEmailChange(event: ComboBox$ChangeEvent): void {
        const claimModel = this.getModel("claim");
        const selectedEmail = (event.getSource()).getSelectedKey().trim();
        const passengersCatalogModel = this.getModel("passengersCatalog");
        const passengersCatalog = passengersCatalogModel?.getData() as Array<IPassenger> ?? [];
        const selectedPassenger = passengersCatalog.find((passenger) => String(passenger.email ?? "") === selectedEmail);

        if (!selectedPassenger) {
            return;
        }

        claimModel.setProperty("/passengerDraft", {
            firstName: String(selectedPassenger.firstName ?? "").trim(),
            lastName: String(selectedPassenger.lastName ?? "").trim(),
            email: String(selectedPassenger.email ?? "").trim(),
            iban: String(selectedPassenger.iban ?? "").trim()
        });

        (this.byId("worksButtonAddPassengerToClaim") as Button).focus();
    }

    public onAddPassengerToClaim(): void {
        const claimModel = this.getModel("claim");
        const passengerDraft = claimModel.getProperty("/passengerDraft") as IPassenger;
        const passengerEntry: IPassenger = {
            firstName: String(passengerDraft.firstName ?? "").trim(),
            lastName: String(passengerDraft.lastName ?? "").trim(),
            email: String(passengerDraft.email ?? "").trim(),
            iban: String(passengerDraft.iban ?? "").trim(),
        };

        if (!passengerEntry.firstName || !passengerEntry.lastName || !passengerEntry.email) {
            return;
        }

        const currentPassengers = (claimModel.getProperty("/passengers") as Array<Record<string, unknown>> | undefined) ?? [];

        claimModel.setProperty("/passengers", [...currentPassengers, passengerEntry]);
        claimModel.setProperty("/passengerDraft", {
            firstName: "",
            lastName: "",
            email: "",
            iban: "",
        });
    }

    public onDeletePassengerFromClaim(event: Button$PressEvent): void {
        const source = event.getSource();
        const passengerPath = source.getBindingContext("claim")?.getPath();
        if (!passengerPath) {
            return;
        }

        const passengerIndex = Number.parseInt(passengerPath.split("/").pop() ?? "-1", 10);
        if (Number.isNaN(passengerIndex)) {
            return;
        }

        const claimModel = this.getModel("claim");
        const passengers = (claimModel.getProperty("/passengers") as Array<Record<string, unknown>> | undefined) ?? [];
        claimModel.setProperty("/passengers", passengers.filter((_, index) => index !== passengerIndex));
    }

    public onClaimDocumentsRemovePress(): void {
        const table = this.byId("idClaimDocumentsTable") as Table;
        if (!table) {
            return;
        }

        const selectedPaths = new Set(
            table.getSelectedItems()
                .map((item) => item.getBindingContext("claim")?.getPath())
                .filter((path): path is string => typeof path === "string")
        );

        if (selectedPaths.size === 0) {
            return;
        }

        const claimModel = this.getModel("claim");
        const documents = (claimModel.getProperty("/documents") as IClaimDocument[] | undefined) ?? [];

        claimModel.setProperty(
            "/documents",
            documents.filter((_, index) => !selectedPaths.has(`/documents/${index}`))
        );
        table.removeSelections(true);
    }

    public onClaimDocumentsUploadDialogAfterClose(): void {
        if (this.dialogDocumentViewer) {
            this.dialogDocumentViewer.setModel(new JSONModel({}), "dialogModel");
        }
    }

    public async onClaimDocumentsUploadBeforeUploadStarts(event: UploadSetwithTable$BeforeUploadStartsEvent): Promise<void> {
        event.preventDefault();
        const item = event.getParameter("item");
        const documentEntry = await this.createIClaimDocument(item.getFileObject() as File);

        const claimModel = this.getModel("claim");
        const documents = claimModel.getProperty("/documents") as IClaimDocument[] | undefined;

        if (documents.length > 0 && documents.find((document) => document.fileName === documentEntry.fileName)) {
            return;
        }

        documents.push(documentEntry);

        claimModel.setProperty("/documents", documents);
    }

    public async onClaimDocumentDetailViewPress(event: Button$PressEvent): Promise<void> {
        const source = event.getSource();
        const documentEntry = this.getDocumentEntry(source);

        if (!this.dialogDocumentViewer) {
            this.dialogDocumentViewer = await Fragment.load({
                id: this.getView().getId(),
                name: "niteroi.packages.air-passenger-rights.fragments.DialogDocumentViewer",
                controller: this
            }) as Dialog;
            this.getView().addDependent(this.dialogDocumentViewer);
        }

        const downloadUrl = await this.getDocumentContent(documentEntry);

        const dialogModel = new JSONModel({imageSrc: "", documentSrc: "", mimeType: "", fileName: ""});
        dialogModel.setProperty("/fileName", documentEntry.fileName);
        dialogModel.setProperty("/mimeType", documentEntry.mimeType);

        this.dialogDocumentViewer.setModel(dialogModel, "dialogModel");
        this.dialogDocumentViewer.open();

        switch (documentEntry.mimeType) {
            case "image/png":
            case "image/jpeg":
            case "image/gif":
                dialogModel.setProperty("/imageSrc", downloadUrl);
                dialogModel.setProperty("/documentSrc", "");
                break;
            case "application/pdf": {
                dialogModel.setProperty("/imageSrc", "");
                dialogModel.setProperty("/documentSrc", downloadUrl);

                const interval = window.setInterval(() => {
                    const iframe = document.querySelector("iframe.sapMPDFViewerContent") as HTMLIFrameElement;

                    const onLoaded = () => {
                        iframe.setAttribute("src", downloadUrl);
                        iframe.contentWindow.removeEventListener("load", onLoaded);

                        window.clearInterval(interval);
                    }

                    if (iframe.contentWindow.document.readyState === "complete") {
                        onLoaded();
                    } else {
                        iframe.contentWindow.addEventListener("load", onLoaded);
                    }
                }, 3000);
                break;
            }
            default:
                dialogModel.setProperty("/imageSrc", "");
                dialogModel.setProperty("/documentSrc", "");
        }
    }

    protected async getDocumentContent(documentEntry: IClaimDocument): Promise<string> {
        const downloadBlob = await this.getDocumentDownloadBlob(documentEntry);
        return window.URL.createObjectURL(downloadBlob);
    }

    protected getDocumentEntry(source: Button): IClaimDocument {
        const documentPath = source.getBindingContext("claim")?.getPath();
        if (!documentPath) {
            return;
        }

        const claimModel = this.getModel("claim");
        const documentEntry = claimModel.getProperty(documentPath) as IClaimDocument | undefined;
        if (!documentEntry?.contentBlob) {
            return;
        }
        return documentEntry;
    }

    public async onClaimDocumentDownloadPress(event: Button$PressEvent): Promise<void> {
        const source = event.getSource();
        const documentEntry = this.getDocumentEntry(source);
        const downloadLink = window.document.createElement("a");
        const downloadUrl = await this.getDocumentContent(documentEntry);

        downloadLink.href = downloadUrl;
        downloadLink.download = documentEntry.fileName;
        downloadLink.click();

        window.URL.revokeObjectURL(downloadUrl);
    }

    private async createIClaimDocument(file: File): Promise<IClaimDocument> {
        const originalMimeType = file.type || this.resolveMimeTypeFromName(file.name);
        const fileBlob = new Blob([await file.arrayBuffer()], {type: originalMimeType || "application/octet-stream"});
        const compressedDocument = await this.compressClaimDocument(fileBlob);

        return {
            fileName: file.name,
            documentType: this.getFileExtension(file.name).toUpperCase(),
            mimeType: originalMimeType || "application/octet-stream",
            fileSize: this.formatKilobytes(compressedDocument.blob.size),
            uploadedAt: new Date().toISOString(),
            contentBlob: compressedDocument.blob,
            contentEncoding: compressedDocument.encoding,
            originalMimeType: originalMimeType || "application/octet-stream"
        };
    }

    private async compressClaimDocument(blob: Blob): Promise<{ blob: Blob; encoding: TDocumentEncoding }> {
        if (typeof window.CompressionStream !== "function") {
            return {blob, encoding: "identity"};
        }

        try {
            const compressedStream = blob.stream().pipeThrough(new window.CompressionStream("gzip"));
            const compressedArrayBuffer = await new Response(compressedStream).arrayBuffer();
            const compressedBlob = new Blob([compressedArrayBuffer], {type: "application/gzip"});

            if (compressedBlob.size >= blob.size) {
                return {blob, encoding: "identity"};
            }

            return {blob: compressedBlob, encoding: "gzip"};
        } catch {
            return {blob, encoding: "identity"};
        }
    }

    private async getDocumentDownloadBlob(documentEntry: IClaimDocument): Promise<Blob> {
        if (documentEntry.contentEncoding !== "gzip") {
            return documentEntry.contentBlob;
        }

        if (typeof window.DecompressionStream !== "function") {
            return documentEntry.contentBlob;
        }

        try {
            const decompressedStream = documentEntry.contentBlob.stream().pipeThrough(new window.DecompressionStream("gzip"));
            const decompressedArrayBuffer = await new Response(decompressedStream).arrayBuffer();
            return new Blob([decompressedArrayBuffer], {type: documentEntry.originalMimeType});
        } catch {
            return documentEntry.contentBlob;
        }
    }

    private getFileExtension(fileName: string): string {
        const fileNameParts = fileName.split(".");
        return fileNameParts.length > 1 ? fileNameParts.pop()?.toLowerCase() ?? "" : "";
    }

    private resolveMimeTypeFromName(fileName: string): string {
        const extension = this.getFileExtension(fileName);
        const mimeTypeByExtension: Record<string, string> = {
            pdf: "application/pdf",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            gif: "image/gif",
            png: "image/png"
        };

        return mimeTypeByExtension[extension] ?? "application/octet-stream";
    }

    private formatKilobytes(sizeInBytes: number): string {
        return (sizeInBytes / 1024).toFixed(1);
    }

    private updateNumericClaimProperty(event: Select$ChangeEvent, propertyPath: string, allowNull = false): void {
        const selectedKey = (event.getSource()).getSelectedKey();
        const claimModel = this.getModel("claim");

        if (selectedKey === "" && allowNull) {
            claimModel.setProperty(propertyPath, null);
            return;
        }

        const normalizedValue = Number.parseInt(selectedKey, 10);
        if (Number.isNaN(normalizedValue)) {
            return;
        }

        claimModel.setProperty(propertyPath, normalizedValue);
    }

    private getClaimStatusOptions(): Array<{ key: ClaimStatuses }> {
        return Object.values(ClaimStatuses)
            .filter((value): value is ClaimStatuses => typeof value === "number")
            .map((value) => ({key: value}));
    }

    private initializeFlightMirrorState(): void {
        const claimModel = this.getModel("claim");
        if (!claimModel) {
            return;
        }

        const flights = claimModel.getProperty("/flights") as Array<Record<string, unknown>> | undefined;
        if (!Array.isArray(flights) || flights.length === 0) {
            claimModel.setProperty("/flights", [this.extractLegacyFlightSegment(claimModel)]);
        }

        const normalizedFlights = (claimModel.getProperty("/flights") as Array<Record<string, unknown>> | undefined) ?? [];
        normalizedFlights.forEach((_, index) => {
            const flightPath = `/flights/${index}`;
            const sameAsOriginal = claimModel.getProperty(`${flightPath}/sameAsOriginalFlight`);
            if (typeof sameAsOriginal !== "boolean") {
                claimModel.setProperty(`${flightPath}/sameAsOriginalFlight`, true);
            }

            if (claimModel.getProperty(`${flightPath}/sameAsOriginalFlight`) === true) {
                this.syncFlightActualWithOriginal(claimModel, flightPath);
            }
        });
    }

    private syncFlightActualWithOriginal(claimModel: JSONModel, flightPath: string): void {
        claimModel.setProperty(`${flightPath}/actualFlightIataCode`, claimModel.getProperty(`${flightPath}/originalFlightIataCode`));
        claimModel.setProperty(`${flightPath}/actualFlightFlightNumber`, claimModel.getProperty(`${flightPath}/originalFlightFlightNumber`));
        claimModel.setProperty(`${flightPath}/actualFlightDepartureAt`, claimModel.getProperty(`${flightPath}/originalFlightDepartureAt`));
        claimModel.setProperty(`${flightPath}/actualFlightArrivalAt`, claimModel.getProperty(`${flightPath}/originalFlightArrivalAt`));
        claimModel.setProperty(`${flightPath}/actualFlightOrigin`, claimModel.getProperty(`${flightPath}/originalFlightOrigin`));
        claimModel.setProperty(`${flightPath}/actualFlightDestination`, claimModel.getProperty(`${flightPath}/originalFlightDestination`));
        claimModel.setProperty(`${flightPath}/actualFlightIncidentType`, claimModel.getProperty(`${flightPath}/originalFlightIncidentType`));
        claimModel.setProperty(`${flightPath}/actualFlightIncidentCondition`, claimModel.getProperty(`${flightPath}/originalFlightIncidentCondition`));
    }

    public syncFlightActualWhenMirrored(event: SearchField$ChangeEvent | Select$ChangeEvent | Input$LiveChangeEvent | ComboBox$ChangeEvent | DateTimeInput$ChangeEvent): void {
        const flightPath = this.getFlightContextPath(event);
        if (!flightPath) {
            return;
        }

        const claimModel = this.getModel("claim");
        if (claimModel.getProperty(`${flightPath}/sameAsOriginalFlight`) === true) {
            this.syncFlightActualWithOriginal(claimModel, flightPath);
        }
    }

    private getFlightContextPath(event: (SearchField$ChangeEvent | Select$ChangeEvent | Input$LiveChangeEvent | ComboBox$ChangeEvent | DateTimeInput$ChangeEvent | CheckBox$SelectEvent)): string | null {
        const source = (event.getSource as () => SearchField | Select | Input | ComboBox | DateTimePicker | CheckBox)();
        return source.getBindingContext("claim")?.getPath() ?? null;
    }

    private loadAirlinesForFlightCodes(): void {
        const requestModel = this.createRequestModel();

        requestModel.loadData(
            "/api/air-passenger-rights/airlines",
            "",
            true,
            "GET",
            false,
            true,
            this.getRequestHeaders()
        ).then(() => {
            const responseData = requestModel.getData();
            const airlinesModel = new JSONModel(responseData);
            airlinesModel.setSizeLimit(2000);
            this.setModel(airlinesModel, "airlines");
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
            this.setModel(new JSONModel({}), "airlines");
        });
    }

    private loadExistingFlightsForClaim(): void {
        const requestModel = this.getModel("flights") ?? this.createRequestModel();

        requestModel.loadData(
            "/api/air-passenger-rights/flights",
            "",
            true,
            "GET",
            false,
            true,
            this.getRequestHeaders()
        ).then(() => {
            const responseData = requestModel.getData();
            const flightsModel = new JSONModel(responseData);
            flightsModel.setSizeLimit(2000);
            this.setModel(flightsModel, "flights");
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
            this.setModel(new JSONModel({}), "flights");
        });
    }

    private loadAirportsForLocations(): void {
        if (this.getModel("airports")) {
            return;
        }

        const airportsModel = this.createRequestModel();
        const headers = this.getRequestHeaders();
        airportsModel.setSizeLimit(10000);
        airportsModel.loadData(
            "/api/air-passenger-rights/airports",
            "",
            true,
            "GET",
            false,
            true,
            headers
        ).then(() => {
            this.setModel(airportsModel, "airports");
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
            this.setModel(new JSONModel({}), "airports");
        });
    }

    private loadPassengersCatalog(): void {
        const passengersCatalogModel = this.createRequestModel();
        passengersCatalogModel.setSizeLimit(10000);
        passengersCatalogModel.loadData(
            "/api/air-passenger-rights/passengers",
            "",
            true,
            "GET",
            false,
            true,
            this.getRequestHeaders()
        ).then(() => {
            this.setModel(passengersCatalogModel, "passengersCatalog");

            const claimModel = this.getModel("claim");
            if (!claimModel.getProperty("/passengerDraft")) {
                claimModel.setProperty("/passengerDraft", {email: "", firstName: "", lastName: "", iban: ""});
            }
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
            this.setModel(new JSONModel([]), "passengersCatalog");
        });
    }

    private extractLegacyFlightSegment(claimModel: JSONModel): Record<string, unknown> {
        const hasLegacyFlightShape = claimModel.getProperty("/originalFlightIataCode") !== undefined
            || claimModel.getProperty("/actualFlightIataCode") !== undefined;

        if (!hasLegacyFlightShape) {
            return this.createEmptyFlightSegment();
        }

        return {
            originalFlightId: claimModel.getProperty("/originalFlightId") ?? "",
            originalFlightIataCode: claimModel.getProperty("/originalFlightIataCode") ?? "",
            originalFlightFlightNumber: claimModel.getProperty("/originalFlightFlightNumber") ?? "",
            originalFlightDepartureAt: claimModel.getProperty("/originalFlightDepartureAt") ?? "",
            originalFlightArrivalAt: claimModel.getProperty("/originalFlightArrivalAt") ?? "",
            originalFlightOrigin: claimModel.getProperty("/originalFlightOrigin") ?? "",
            originalFlightDestination: claimModel.getProperty("/originalFlightDestination") ?? "",
            originalFlightIncidentType: claimModel.getProperty("/originalFlightIncidentType") ?? 1,
            originalFlightIncidentCondition: claimModel.getProperty("/originalFlightIncidentCondition") ?? 0,
            sameAsOriginalFlight: claimModel.getProperty("/sameAsOriginalFlight") ?? true,
            actualFlightId: claimModel.getProperty("/actualFlightId") ?? "",
            actualFlightIataCode: claimModel.getProperty("/actualFlightIataCode") ?? "",
            actualFlightFlightNumber: claimModel.getProperty("/actualFlightFlightNumber") ?? "",
            actualFlightDepartureAt: claimModel.getProperty("/actualFlightDepartureAt") ?? "",
            actualFlightArrivalAt: claimModel.getProperty("/actualFlightArrivalAt") ?? "",
            actualFlightOrigin: claimModel.getProperty("/actualFlightOrigin") ?? "",
            actualFlightDestination: claimModel.getProperty("/actualFlightDestination") ?? "",
            actualFlightIncidentType: claimModel.getProperty("/actualFlightIncidentType") ?? 1,
            actualFlightIncidentCondition: claimModel.getProperty("/actualFlightIncidentCondition") ?? 0
        };
    }

    private createEmptyFlightSegment(): Record<string, unknown> {
        return {
            originalFlightId: "",
            originalFlightIataCode: "",
            originalFlightFlightNumber: "",
            originalFlightDepartureAt: "",
            originalFlightArrivalAt: "",
            originalFlightOrigin: "",
            originalFlightDestination: "",
            originalFlightIncidentType: 0,
            originalFlightIncidentCondition: 0,
            sameAsOriginalFlight: true,
            actualFlightId: "",
            actualFlightIataCode: "",
            actualFlightFlightNumber: "",
            actualFlightDepartureAt: "",
            actualFlightArrivalAt: "",
            actualFlightOrigin: "",
            actualFlightDestination: "",
            actualFlightIncidentType: 0,
            actualFlightIncidentCondition: 0
        };
    }

    private createEmptyClaimModel(): JSONModel {
        const createdAt = DateFormat.getDateTimeInstance({pattern: "yyyy-MM-dd HH:mm:ss"}).format(new Date());
        return new JSONModel({
            claimId: "",
            bookingReference: "",
            claimStatus: ClaimStatuses.DRAFT,
            notes: "",
            company_id: "",
            createdBy: "",
            isActive: true,
            createdAt: createdAt,
            flights: [this.createEmptyFlightSegment()],
            passengerDraft: {
                firstName: "",
                lastName: "",
                email: "",
                iban: ""
            },
            passengers: [],
            documents: [],
            historyEntries: []
        });
    }
}
